import { useCallback, useEffect, useRef } from 'react';

import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type AccessToken,
  clearAuthTokens,
  getAccessToken,
  getAuthTokens,
  getRefreshToken,
  isTokenExpired,
  setAuthTokens,
} from '@/lib/tokenStorage';

import { authFetch, authServiceUrl } from './authService';

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  returnUrl?: string;
}

export interface ISite {
  siteId: string;
  companyId: string;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  isActive: boolean;
  createdDate: string;
  locationId: string;
  acctSite: string | null;
  csLocationId: string | null;
  defaultYardId: string | null;
  commBillingDonorHousing: number;
  billGrade3Frz: number;
  billGrade3: number;
  vetAssigned: string | null;
  billDrugsType: number;
  billStorageFees: number;
  ivfBillGroup: string;
  region: number;
  fertSite: string;
  newRelatedGl: string | null;
  glSite: string | null;
  opuTech: string | null;
  pseTech: string | null;
  csr: string | null;
  tripFee: number | null;
  maxTransfers: number | null;
  maxProcs: number | null;
  siteType: number;
  siteCapacity: number | null;
}

export interface PaginatedResponse<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface SitesQueryParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
}

export interface Permission {
  permissionId: string;
  applicationId: string;
  applicationName: string;
  code: string;
  name: string;
  description?: string;
  permissionType: string;
  resourceName: string;
  scope: string;
  isActive: boolean;
  createdDate: string;
}

export interface CurrentUser {
  userId: string;
  azureObjectId: string;
  email: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  companyName?: string;
  mobilePhone?: string;
  preferredLanguage?: string;
  accountEnabled: boolean;
  mfaStatus?: string;
  lastSignInDateTime?: string;
  roles: Array<{ roleId: string }>;
  companies: Array<{
    companyId: string;
    name: string;
    isActive: boolean;
    crmUrl?: string;
    code: string;
  }>;
  permissions?: Permission[];
  sites: ISite[];
}

export interface SessionStatus {
  isAuthenticated: boolean;
  azureObjectId?: string;
  email?: string;
  expiresAt?: string;
  sessionExpired: boolean;
}

async function refreshAccessToken(signal?: AbortSignal): Promise<AuthResponse> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const res = await authFetch('/api/auth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
    signal,
  });

  // Tokens saved while this was in flight must not be overwritten
  signal?.throwIfAborted();

  if (!res.ok) {
    // Another tab may have rotated the refresh token while this was in flight
    if (getRefreshToken() === refreshToken) {
      clearAuthTokens();
    }
    throw new Error('Failed to refresh token');
  }

  const data: AuthResponse = await res.json();
  signal?.throwIfAborted();
  setAuthTokens(data);
  return data;
}

/**
 * Hook to get current authenticated user
 */
export function useCurrentUser() {
  const token = useAccessToken();

  return useQuery({
    queryKey: ['currentUser'],
    async queryFn(): Promise<CurrentUser> {
      if (!token) {
        throw new Error('No access token available');
      }

      const res = await authFetch('/api/auth/me', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          clearAuthTokens();
          throw new Error('Unauthorized');
        }
        throw new Error('Failed to fetch user profile');
      }

      return res.json();
    },
    enabled: !!token && !isTokenExpired(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/** Saves new tokens and shares them with every `useAccessToken` caller. */
export function saveAuthTokens(
  queryClient: QueryClient,
  tokens: Parameters<typeof setAuthTokens>[0],
) {
  setAuthTokens(tokens);
  // An in-flight refresh must not overwrite the tokens saved here
  void queryClient.cancelQueries({
    queryKey: accessTokenQueryOptions().queryKey,
  });
  queryClient.setQueryData(
    accessTokenQueryOptions().queryKey,
    tokens.accessToken,
  );
}

// Every caller shares one query, so concurrent refreshes reuse one request
export function accessTokenQueryOptions() {
  const REFRESH_BEFORE_EXPIRY_MS = 60_000;
  const MAX_JITTER_MS = 30_000;

  return queryOptions({
    queryKey: ['accessToken'],
    async queryFn({ signal }) {
      return (await refreshAccessToken(signal)).accessToken;
    },
    initialData: getAccessToken,
    staleTime: Infinity,
    refetchInterval() {
      const tokens = getAuthTokens();

      return tokens
        ? Math.max(
            1000,
            tokens.expiresIn * 1000 -
              REFRESH_BEFORE_EXPIRY_MS -
              Math.random() * MAX_JITTER_MS,
          )
        : false;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Current access token, refreshed in the background shortly before it expires.
 */
export function useAccessToken(): AccessToken {
  const query = useQuery(accessTokenQueryOptions());
  return query.isError ? null : (query.data ?? null);
}

/**
 * The access token for callbacks that run later, such as AG Grid `getRows`.
 * Still subscribes so the token keeps refreshing, but it can be read without
 * adding the token to dependency lists, so a refresh doesn't rebuild the
 * callback. Use `useAccessToken` for anything that should react to a new token.
 */
export function useAccessTokenRef(): { readonly current: AccessToken } {
  const token = useAccessToken();
  const ref = useRef(token);

  useEffect(() => {
    ref.current = token;
  }, [token]);

  return ref;
}

/**
 * Hook to logout user
 */
export function useLogout() {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    async mutationFn() {
      if (token) {
        try {
          await authFetch('/api/auth/logout', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          console.error('Logout request failed:', error);
        }
      }

      clearAuthTokens();
    },
    onSettled: () =>
      queryClient.setQueryData(accessTokenQueryOptions().queryKey, null),
  });

  const logout = useCallback(() => mutation.mutateAsync(), [mutation]);

  return [logout, mutation] as const;
}

/**
 * Hook to check session status
 */
export function useSessionStatus() {
  const token = useAccessToken();

  return useQuery({
    queryKey: ['sessionStatus'],
    async queryFn(): Promise<SessionStatus> {
      if (!token) {
        return {
          isAuthenticated: false,
          sessionExpired: true,
        };
      }

      try {
        const res = await authFetch('/api/auth/session-status', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          return {
            isAuthenticated: false,
            sessionExpired: true,
          };
        }

        return res.json();
      } catch (error) {
        console.error('Failed to check session status:', error);
        return {
          isAuthenticated: false,
          sessionExpired: true,
        };
      }
    },
    refetchInterval: 60000,
  });
}

export function useSwitchDatabase() {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    async mutationFn(companyId: string): Promise<AuthResponse> {
      if (!token) {
        throw new Error('No access token available');
      }

      const res = await authFetch('/api/auth/switch-database', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companyId }),
      });

      if (!res.ok) {
        if (res.status === 401) {
          clearAuthTokens();
          throw new Error('Unauthorized - insufficient permissions');
        }
        if (res.status === 403) {
          throw new Error('Forbidden - DATABASE.SWITCH permission required');
        }
        if (res.status === 404) {
          throw new Error('Company not found or user does not have access');
        }
        throw new Error('Failed to switch database');
      }

      const data: AuthResponse = await res.json();
      saveAuthTokens(queryClient, data);
      return data;
    },
  });

  const switchDb = useCallback(
    (companyId: string) => mutation.mutateAsync(companyId),
    [mutation],
  );

  return [switchDb, mutation] as const;
}

/**
 * Hook to update the current user's display name in B2C
 */
export function useUpdateUserProfile() {
  const token = useAccessToken();
  const mutation = useMutation({
    async mutationFn(params: { azureObjectId: string; displayName: string }) {
      if (!token) throw new Error('No access token available');

      const res = await fetch(
        authServiceUrl(`/api/B2CManagement/${params.azureObjectId}`),
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ DisplayName: params.displayName }),
        },
      );

      if (!res.ok) {
        if (res.status === 401) {
          clearAuthTokens();
          throw new Error('Unauthorized');
        }
        throw new Error('Failed to update display name');
      }
    },
  });

  const update = useCallback(
    (azureObjectId: string, fields: { displayName: string }) =>
      mutation.mutateAsync({ azureObjectId, ...fields }),
    [mutation],
  );

  return [update, mutation] as const;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  user: CurrentUser | null | undefined,
  permissionCode: string,
): boolean {
  if (!user || !user.permissions) return false;
  return user.permissions.some(
    (p) => p.code.toUpperCase() === permissionCode.toUpperCase(),
  );
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
  user: CurrentUser | null | undefined,
  permissionCodes: string[],
): boolean {
  return permissionCodes.some((code) => hasPermission(user, code));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
  user: CurrentUser | null | undefined,
  permissionCodes: string[],
): boolean {
  return permissionCodes.every((code) => hasPermission(user, code));
}

export function getValidSites(
  user: CurrentUser | null | undefined,
): ISite[] | undefined {
  if (!user) return [];

  if (hasPermission(user, 'NUCLEUS.SELECT_SITES')) {
    return user.sites || [];
  }

  return undefined;
}

export const MODULES = {
  ANIMAL_MANAGEMENT: 'ANIMAL_MANAGEMENT',
  ASPIRATION: 'ASPIRATION',
  BULK_DONOR_SCHEDULING: 'BULK_DONOR_SCHEDULING',
  CALF_ENTRY: 'CALF_ENTRY',
  CHUTE_SCHEDULER: 'CHUTE_SCHEDULER',
  DAY7: 'DAY7',
  DONOR_SCHEDULING_PARTNERSHIP: 'DONOR_SCHEDULING_PARTNERSHIP',
  FERTILIZATION: 'FERTILIZATION',
  FERT_WORK_READ: 'FERT_WORK.READ',
  FERT_WORK_WRITE: 'FERT_WORK.WRITE',
  FREEZE: 'FREEZE',
  FRESH_SHIPMENT: 'FRESH_SHIPMENT',
  FRESH_STRAW: 'FRESH_STRAW',
  OF_TRANSFER: 'OF_TRANSFER',
  OF_TRANSFER_READ: 'OF_TRANSFER.READ',
  OF_TRANSFER_WRITE: 'OF_TRANSFER.WRITE',
  ON_FARM_IMPLANT: 'ON_FARM_IMPLANT',
  OPU_SEARCH: 'OPU_SEARCH',
  SEMEN: 'SEMEN',
  SEMEN_PULL: 'SEMEN_PULL',
  SERVICE_SCHEDULER: 'SERVICE_SCHEDULER',
  SHIPPING: 'SHIPPING',
  SHIPPING_DETAILS: 'SHIPPING_DETAILS',
  STRIPPING: 'STRIPPING',
  ISSUES: 'ISSUES',
  DATABASE: 'DATABASE.SWITCH',
  LEGACY_APPS: 'NUCLEUS.LEGACY_APPS',
  EMBRYO_READ: 'EMBRYO.READ',
  EMBRYO_WRITE: 'EMBRYO.WRITE',
  EMBRYO_MANAGER_READ: 'EMBRYO_MANAGER.READ',
  EMBRYO_MANAGER_WRITE: 'EMBRYO_MANAGER.WRITE',
  CHUTE_IMPLANT_READ: 'CHUTE_IMPLANT.READ',
  CHUTE_IMPLANT_WRITE: 'CHUTE_IMPLANT.WRITE',
} as const;

const NUCLEUS_API_APP_ID = '11111111-1111-1111-1111-111111111111';
const NUCLEUS_UI_APP_ID = '22222222-2222-2222-2222-222222222222';

/**
 * Check if user has access to Nucleus application
 * User must have at least one permission for TOG-NUCLEUS-API or TOG-NUCLEUS-UI
 */
export function hasNucleusAccess(
  user: CurrentUser | null | undefined,
): boolean {
  if (!user) {
    return false;
  }

  if (!user.companies || user.companies.length === 0) {
    return false;
  }

  if (!user.roles || user.roles.length === 0) {
    return false;
  }

  if (!user.permissions || user.permissions.length === 0) {
    return false;
  }

  return user.permissions.some(
    (p) =>
      p.applicationId === NUCLEUS_API_APP_ID ||
      p.applicationId === NUCLEUS_UI_APP_ID,
  );
}

export function getNucleusAccessErrorMessage(
  user: CurrentUser | null | undefined,
): string {
  if (!user) {
    return 'User not found. Please try logging in again.';
  }

  if (!user.companies || user.companies.length === 0) {
    return 'Your account is not associated with any company. Please contact the help desk for assistance with your account setup.';
  }

  if (!user.roles || user.roles.length === 0) {
    return 'Welcome! Your account has been created successfully. However, your access is currently on hold pending approval. Please reach out to the help desk to complete your account setup and gain access to Nucleus.';
  }

  if (!user.permissions || user.permissions.length === 0) {
    return 'Your account setup is incomplete. Please contact the help desk to verify your access permissions.';
  }

  return 'You do not have the required permissions to access Nucleus. Please contact the help desk to request access.';
}

function normalizeSiteDto(site: any): ISite {
  return {
    siteId: site.siteId ?? site.SiteId ?? '',
    companyId: site.companyId ?? site.CompanyId ?? '',
    code: site.code ?? site.Code ?? '',
    name: site.name ?? site.Name ?? '',
    address: site.address ?? site.Address ?? null,
    city: site.city ?? site.City ?? null,
    state: site.state ?? site.State ?? null,
    postalCode: site.postalCode ?? site.PostalCode ?? null,
    country: site.country ?? site.Country ?? null,
    isActive: site.isActive ?? site.IsActive ?? true,
    createdDate: site.createdDate ?? site.CreatedDate ?? '',
    locationId: site.locationId ?? site.LocationId ?? '',
    acctSite: site.acctSite ?? site.AcctSite ?? null,
    csLocationId: site.csLocationId ?? site.CsLocationId ?? null,
    defaultYardId: site.defaultYardId ?? site.DefaultYardId ?? null,
    commBillingDonorHousing:
      site.commBillingDonorHousing ?? site.CommBillingDonorHousing ?? 0,
    billGrade3Frz: site.billGrade3Frz ?? site.BillGrade3Frz ?? 0,
    billGrade3: site.billGrade3 ?? site.BillGrade3 ?? 0,
    vetAssigned: site.vetAssigned ?? site.VetAssigned ?? null,
    billDrugsType: site.billDrugsType ?? site.BillDrugsType ?? 0,
    billStorageFees: site.billStorageFees ?? site.BillStorageFees ?? 0,
    ivfBillGroup: site.ivfBillGroup ?? site.IvfBillGroup ?? '',
    region: site.region ?? site.Region ?? 0,
    fertSite: site.fertSite ?? site.FertSite ?? '',
    newRelatedGl: site.newRelatedGl ?? site.NewRelatedGl ?? null,
    glSite: site.glSite ?? site.GlSite ?? null,
    opuTech: site.opuTech ?? site.OpuTech ?? null,
    pseTech: site.pseTech ?? site.PseTech ?? null,
    csr: site.csr ?? site.Csr ?? null,
    tripFee: site.tripFee ?? site.TripFee ?? null,
    maxTransfers: site.maxTransfers ?? site.MaxTransfers ?? null,
    maxProcs: site.maxProcs ?? site.MaxProcs ?? null,
    siteType: site.siteType ?? site.SiteType ?? 0,
    siteCapacity: site.siteCapacity ?? site.SiteCapacity ?? null,
  };
}

export function useSitesPage(params: SitesQueryParams, enabled = true) {
  const token = useAccessToken();

  return useQuery({
    queryKey: [
      'sites-page',
      params.pageNumber ?? 1,
      params.pageSize ?? 100,
      params.searchTerm ?? '',
    ],
    async queryFn(): Promise<PaginatedResponse<ISite>> {
      if (!token) {
        throw new Error('No access token available');
      }

      const pageNumber = params.pageNumber ?? 1;
      const pageSize = params.pageSize ?? 100;
      const url = authServiceUrl('/api/Sites');
      url.searchParams.set('pageNumber', String(pageNumber));
      url.searchParams.set('pageSize', String(pageSize));
      url.searchParams.set('isActive', 'true');
      url.searchParams.set('sortBy', 'Name');
      if (params.searchTerm?.trim()) {
        url.searchParams.set('searchTerm', params.searchTerm.trim());
      }

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          clearAuthTokens();
          throw new Error('Unauthorized');
        }
        throw new Error('Failed to fetch sites');
      }

      const payload = (await res.json()) as any;
      const rawItems = payload.items ?? payload.Items ?? [];
      return {
        items: rawItems.map(normalizeSiteDto),
        pageNumber: payload.pageNumber ?? payload.PageNumber ?? pageNumber,
        pageSize: payload.pageSize ?? payload.PageSize ?? pageSize,
        totalCount: payload.totalCount ?? payload.TotalCount ?? rawItems.length,
        totalPages: payload.totalPages ?? payload.TotalPages ?? 1,
        hasPrevious: payload.hasPrevious ?? payload.HasPrevious ?? false,
        hasNext: payload.hasNext ?? payload.HasNext ?? false,
      };
    },
    enabled: enabled && !!token && !isTokenExpired(),
    staleTime: 60 * 1000,
  });
}
